import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {api, ApiContext} from '../../api';
import {ApiError} from '../../ApiError';
import {bodyColorAccent} from '../../styles';
import {Errors} from '../../types/Errors';
import {Params} from '../../types/Params';
import {Resource} from '../../types/Resource';
import {ResourceDetails} from '../../types/ResourceDetails';
import {Dialog} from '../Dialog';
import {Spinner} from '../Spinner';
import {IntlShape, useIntl} from 'react-intl';
import {getMessage} from '../../messages';
import {getDisplayName} from '../displayName';

type Props = {
  ctx: ApiContext;
  shown: boolean;
  appId: string;
  resId: string;
  params?: Params;
  onAfter: (item: ResourceDetails) => void;
  onClose: (item: ResourceDetails) => void;
};

export const DeleteDialog = (props: Props) => {
  const ref = useRef<Dialog>(null);
  const [res, setRes] = useState<Resource | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const intl = useIntl();

  useEffect(() => {
    if (props.shown && ref && ref.current) {
      ref.current.showDialog();
    }
  }, [props.shown, ref]);

  const onOpen = async () => {
    try {
      const res = await api.newDelete(
        props.ctx,
        props.appId,
        props.resId,
        props.params,
      );
      setRes(res);
    } catch (e) {}
  };

  const onSave = async () => {
    if (!res) {
      return false;
    }

    try {
      await api.remove(props.ctx, res.schema.id, res.id);
      props.onAfter(res.details);
      return true;
    } catch (e) {
      if (e instanceof ApiError) {
        setErrors(e.getMessageMap());
      }

      return false;
    }
  };

  return (
    <Dialog
      ref={ref}
      onExecute={onSave}
      onOpen={onOpen}
      onCancel={dummyToShowCancelButton}
      onClose={() => {
        setErrors({});

        if (res) {
          props.onClose(res.details);
        }
      }}
      errors={errors['_']}
      title={getMessage(intl, 'Action.Delete', 'Delete')}
      renderBody={() => {
        if (!res) {
          return <Spinner />;
        }

        return renderBody(intl, res.details);
      }}
      executeLabel={getMessage(intl, 'Delete.Execute', 'Delete')}
      maxWidth={'90vw'}
    />
  );
};

function dummyToShowCancelButton() {}

function renderBody(
  intl: IntlShape,
  item?: ResourceDetails,
): JSX.Element | null {
  if (!item) {
    return null;
  }

  return (
    <MessageContainer>
      <p>
        {getMessage(intl, 'Delete.TryPrefix', 'You are trying to delete ')}
        <ItemName>
          {getDisplayName(item) ||
            getMessage(intl, 'Delete.TargetName', 'resource')}
        </ItemName>
        {getMessage(intl, 'Delete.TrySuffix', '.')}
      </p>
      <p>
        {getMessage(
          intl,
          'Delete.Confirm',
          'Are you sure you want to delete it?',
        )}
      </p>
    </MessageContainer>
  );
}

const MessageContainer = styled.div`
  padding-left: 1rem;
  padding-right: 1rem;
  line-height: 1.5;
  font-size: 1rem;
  max-width: 600px;

  & p {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

const ItemName = styled.span`
  background-color: ${bodyColorAccent};
  padding: 0.3rem;
`;
